import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Pages
import Dashboard from './pages/Dashboard';
import Discovery from './pages/Discovery';
import RestaurantDetail from './pages/RestaurantDetail';
import Stats from './pages/Stats';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Admin from './pages/Admin';
import Wrapped from './pages/Wrapped';
import Settings from './pages/Settings';
import TasteMap from './pages/TasteMap';
import Concierge from './pages/Concierge';
import Challenges from './pages/Challenges';
import CheckIn from './pages/CheckIn';
import Timeline from './pages/Timeline';
import FavouriteDishes from './pages/FavouriteDishes';
import Collections from './pages/Collections';

// Components
import Header from './components/Header';

import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const login = useCallback((newToken) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        console.error('Auth error:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [token, logout]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F5] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A94438] mx-auto"></div>
          <p className="mt-4 text-[#7A7873]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Layout with Header
const AppLayout = ({ children, showHeader = true }) => {
  return (
    <div className="min-h-screen bg-[#F9F8F5]">
      {showHeader && <Header />}
      <main>{children}</main>
    </div>
  );
};

// API client with auth
export const createApiClient = (token) => {
  const client = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  client.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return client;
};

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/discovery" element={
            <AppLayout>
              <Discovery />
            </AppLayout>
          } />
          
          {/* Protected routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/restaurants/:id" element={
            <ProtectedRoute>
              <AppLayout>
                <RestaurantDetail />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/stats" element={
            <ProtectedRoute>
              <AppLayout>
                <Stats />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AppLayout>
                <Admin />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/wrapped" element={
            <ProtectedRoute>
              <AppLayout>
                <Wrapped />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <AppLayout>
                <Settings />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/map" element={
            <ProtectedRoute>
              <AppLayout>
                <TasteMap />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/concierge" element={
            <ProtectedRoute>
              <AppLayout>
                <Concierge />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/challenges" element={
            <ProtectedRoute>
              <AppLayout>
                <Challenges />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/checkin" element={
            <ProtectedRoute>
              <AppLayout>
                <CheckIn />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/timeline" element={
            <ProtectedRoute>
              <AppLayout>
                <Timeline />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/dishes" element={
            <ProtectedRoute>
              <AppLayout>
                <FavouriteDishes />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/collections" element={
            <ProtectedRoute>
              <AppLayout>
                <Collections />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
